import React, { useRef, useState, useCallback, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import * as XLSX from 'xlsx';
import { fetchOperators } from '../Services/FirebaseUtils';

export default function CsvImporter({ open, onClose, onSuccess, onError }) {
  const fileInputRef = useRef();
  const [error, setError] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [parsedRows, setParsedRows] = useState(null);
  const [columnsValid, setColumnsValid] = useState(false);
  const [operatorNames, setOperatorNames] = useState([]);
  const requiredFields = ['centerName', 'operatorName', 'city', 'micromarket', 'address', 'locality', 'landmark', 'googleMapsLocation', 'latitude', 'longitude', 'listPrice', 'active'];
  console.log(operatorNames, "operatorNames")
  // Fetch operator names on open
  useEffect(() => {
    if (open) {
      fetchOperators().then(result => {
        const names = (result.operators || []).map(op => op.operatorName?.trim()).filter(Boolean);
        setOperatorNames(names.map(n => n.toLowerCase()));
      });
    }
  }, [open]);

  // Validate columns and data
  const validateColumnsAndData = useCallback((rows) => {
    if (!rows || !rows.length) return { valid: false, error: 'No rows found.' };
    for (let i = 0; i < rows.length; i++) {
      for (const field of requiredFields) {
        if (!rows[i][field] || rows[i][field].toString().trim() === '') {
          return { valid: false, error: `Row ${i + 2}: Field '${field}' is empty.` };
        }
      }
      // Operator existence check (case-insensitive)
      if (!operatorNames.includes((rows[i].operatorName || '').trim().toLowerCase())) {
        return { valid: false, error: `Row ${i + 2}: Operator '${rows[i].operatorName}' does not exist.` };
      }
    }
    return { valid: true };
  }, [requiredFields, operatorNames]);

  const parseFile = (file) => {
    setError(null);
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = evt.target.result;
        let rows = [];
        if (file.name.endsWith('.csv')) {
          const workbook = XLSX.read(data, { type: 'string' });
          const ws = workbook.Sheets[workbook.SheetNames[0]];
          rows = XLSX.utils.sheet_to_json(ws);
        } else if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
          const workbook = XLSX.read(data, { type: 'binary' });
          const ws = workbook.Sheets[workbook.SheetNames[0]];
          rows = XLSX.utils.sheet_to_json(ws);
        } else {
          throw new Error('Only CSV or Excel files are supported');
        }
        if (rows.length === 0) throw new Error('No data found in file');
        const validation = validateColumnsAndData(rows);
        setParsedRows(validation.valid ? rows : null);
        setColumnsValid(validation.valid);
        setSelectedFile(file);
        setError(validation.error || null);
      } catch (err) {
        setError(err.message);
        setParsedRows(null);
        setColumnsValid(false);
        setSelectedFile(null);
        onError && onError(err.message);
      }
    };
    if (file.name.endsWith('.csv')) {
      reader.readAsText(file);
    } else {
      reader.readAsBinaryString(file);
    }
  };

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    parseFile(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      parseFile(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragActive(false);
  };

  const handleUpload = () => {
    if (parsedRows && columnsValid) {
      onSuccess(parsedRows);
      setParsedRows(null);
      setSelectedFile(null);
      setColumnsValid(false);
    }
  };

  const handleClose = () => {
    setParsedRows(null);
    setSelectedFile(null);
    setColumnsValid(false);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Import Centers from CSV</DialogTitle>
      <DialogContent>
        <Box
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          sx={{
            border: dragActive ? '2px dashed #1976d2' : '2px dashed #ccc',
            borderRadius: 2,
            p: 3,
            textAlign: 'center',
            bgcolor: dragActive ? '#f0f7ff' : 'background.paper',
            mb: 2,
            cursor: 'pointer',
            transition: 'border 0.2s, background 0.2s'
          }}
          onClick={() => fileInputRef.current?.click()}
        >
          <Typography variant="body2" color="textSecondary">
            Drag and drop your CSV or Excel file here, or click to select
          </Typography>
          <input
            type="file"
            accept=".csv,.xlsx,.xls"
            hidden
            ref={fileInputRef}
            onChange={handleFile}
          />
          {selectedFile && (
            <Typography variant="caption" color="primary" sx={{ mt: 1, display: 'block' }}>
              Selected: {selectedFile.name}
            </Typography>
          )}
        </Box>
        {parsedRows && !columnsValid && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          color="primary"
          disabled={!parsedRows || !columnsValid}
        >
          Upload CSV
        </Button>
      </DialogActions>
    </Dialog>
  );
}
